import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from '@edx/frontend-platform/i18n';

import SlAlert from '@shoelace-style/shoelace/dist/react/alert';
import SlIcon from '@shoelace-style/shoelace/dist/react/icon';
import type SlAlertElement from '@shoelace-style/shoelace/dist/components/alert/alert';

import { cohortsApi } from '../../api';
import type { UpdateCohortPayload } from '../../api/cohorts';
import { useCohortInfo, useDebounce } from '../../hooks';
import { copyTextToClipboard } from '../../utils';
import { resolveApiErrorMessage } from '../../utils/apiErrors';

import { useCohortLayoutContext } from './CohortLayout';
import { avatarColor, initialsOf } from './utils';
import messages from './messages';

const NAME_MAX_LENGTH = 255;
const DESCRIPTION_MAX_LENGTH = 2000;
const SLUG_MAX_LENGTH = 280;

const buildInviteLink = (slug: string): string => (
  `${window.location.origin}/groups/join/${slug}`
);

interface CopyNotification {
  success: boolean;
  msg: string;
}

interface InfoFieldProps {
  label: string;
  children: React.ReactNode;
}

/**
 * One labelled field of the cohort info card. Kept as its own presentational
 * unit so the value can swap to an inline editor without reworking the layout.
 */
const InfoField: React.FC<InfoFieldProps> = ({ label, children }) => (
  <div className="cohort-info-field">
    <div className="cohort-info-field-label">{label}</div>
    <div className="cohort-info-field-value">{children}</div>
  </div>
);

/**
 * Cohort Info view: a summary card — name, description, course, facilitator,
 * member count, created date — plus an invite-link box with copy-to-clipboard.
 * Owners and co-facilitators get an inline editor for name/description, with
 * client validation mirroring the backend; a successful save pushes the fresh
 * detail into the layout context so the sidebar/breadcrumb re-render.
 *
 * The owner additionally gets the Cohort URL (slug) field: a change is warned
 * about inline before saving, and a rename rewrites the browser URL too.
 */
const CohortInfo: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { detail, setDetail } = useCohortLayoutContext();
  const { info } = useCohortInfo(detail.slug);

  const [notification, setNotification] = useState<CopyNotification | null>(null);
  const notificationRef = useRef<SlAlertElement | null>(null);

  const canEdit = detail.my_role === 'owner' || detail.my_role === 'co_facilitator';
  // Renaming the invite URL is owner-only on the backend, so a co-facilitator
  // never even sees the field.
  const canEditUrl = detail.my_role === 'owner';

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editName, setEditName] = useState<string>(detail.name);
  const [editDescription, setEditDescription] = useState<string>(detail.description ?? '');
  const [editSlug, setEditSlug] = useState<string>(detail.slug);
  const [nameError, setNameError] = useState<string | null>(null);
  const [descriptionError, setDescriptionError] = useState<string | null>(null);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Leave edit mode if the viewed cohort changes underneath us, so edits to one
  // cohort never bleed into the next.
  useEffect(() => {
    setIsEditing(false);
    setNameError(null);
    setDescriptionError(null);
    setSlugError(null);
  }, [detail.slug]);

  // Fire the Shoelace toast once a new notification is set.
  useDebounce(() => {
    if (notification?.msg && notificationRef.current && 'toast' in notificationRef.current) {
      notificationRef.current?.toast();
    }
  }, 200, [notification]);

  const inviteLink = buildInviteLink(detail.invite_slug);
  const ownerName = info?.owner_name ?? '';

  const handleCopyLink = async (): Promise<void> => {
    // `copyTextToClipboard` falls back to the legacy path when
    // `navigator.clipboard` is missing — it is absent over plain HTTP, which is
    // how the portal is served locally, so the direct API call always failed.
    const copied = await copyTextToClipboard(inviteLink);
    setNotification(copied
      ? { success: true, msg: intl.formatMessage(messages.inviteCopiedToast) }
      : { success: false, msg: intl.formatMessage(messages.inviteCopyErrorToast) });
  };

  const startEdit = (): void => {
    setEditName(detail.name);
    setEditDescription(detail.description ?? '');
    setEditSlug(detail.slug);
    setNameError(null);
    setDescriptionError(null);
    setSlugError(null);
    setIsEditing(true);
  };

  const cancelEdit = (): void => {
    setIsEditing(false);
    setNameError(null);
    setDescriptionError(null);
    setSlugError(null);
  };

  const trimmedSlug = editSlug.trim();
  // Warning shown as soon as the value differs, before the facilitator commits.
  const slugHasChanged = canEditUrl && trimmedSlug !== detail.slug;

  const handleSave = (): void => {
    // DRF trims whitespace before validating and storing, so mirror that here
    // for both validation and the payload we send.
    const trimmedName = editName.trim();
    const trimmedDescription = editDescription.trim();

    let valid = true;
    if (!trimmedName) {
      setNameError(intl.formatMessage(messages.infoNameRequired));
      valid = false;
    } else if (trimmedName.length > NAME_MAX_LENGTH) {
      setNameError(intl.formatMessage(messages.infoNameTooLong));
      valid = false;
    } else {
      setNameError(null);
    }

    if (trimmedDescription.length > DESCRIPTION_MAX_LENGTH) {
      setDescriptionError(intl.formatMessage(messages.infoDescriptionTooLong));
      valid = false;
    } else {
      setDescriptionError(null);
    }

    if (canEditUrl) {
      if (!trimmedSlug) {
        setSlugError(intl.formatMessage(messages.infoUrlRequired));
        valid = false;
      } else if (trimmedSlug.length > SLUG_MAX_LENGTH) {
        setSlugError(intl.formatMessage(messages.infoUrlInvalid));
        valid = false;
      } else {
        setSlugError(null);
      }
    }

    if (!valid) {
      return;
    }

    // Send only changed fields (the PATCH is partial; empty body is a no-op).
    const payload: UpdateCohortPayload = {};
    if (trimmedName !== detail.name) {
      payload.name = trimmedName;
    }
    if (trimmedDescription !== (detail.description ?? '')) {
      payload.description = trimmedDescription;
    }
    if (slugHasChanged) {
      payload.slug = trimmedSlug;
    }

    if (Object.keys(payload).length === 0) {
      setIsEditing(false);
      return;
    }

    const isSlugChange = payload.slug !== undefined;
    setIsSaving(true);

    cohortsApi.updateCohort(
      detail.slug,
      payload,
      (updated) => {
        setIsSaving(false);
        setIsEditing(false);
        // Push the fresh detail up so the sidebar + breadcrumb re-render without
        // a refetch, and the invite box shows the renamed link.
        setDetail(updated);
        setEditSlug(updated.slug);
        if (isSlugChange) {
          // The route param IS the slug, so the browser URL is now stale —
          // replace it so a reload (and any copied page URL) still resolves.
          navigate(`/cohorts/${updated.slug}/info`, { replace: true });
          setNotification({
            success: true,
            msg: intl.formatMessage(messages.infoToastUrlSaved, {
              link: buildInviteLink(updated.invite_slug),
            }),
          });
          return;
        }
        setNotification({ success: true, msg: intl.formatMessage(messages.infoToastSaved) });
      },
      (error) => {
        setIsSaving(false);
        const status = (error as { status?: number })?.status;
        const errorPayload = (error as { payload?: unknown })?.payload;

        // Slug conflicts/rejections come back as machine codes, so they get a
        // specific message on the field rather than the generic toast.
        const slugCode = (errorPayload as { error?: string } | undefined)?.error;
        if (slugCode === 'slug_taken' || slugCode === 'invalid_slug'
            || slugCode === 'owner_role_required_for_slug') {
          setSlugError(resolveApiErrorMessage(
            error,
            {
              slug_taken: intl.formatMessage(messages.infoUrlTaken),
              invalid_slug: intl.formatMessage(messages.infoUrlInvalid),
              owner_role_required_for_slug: intl.formatMessage(messages.infoUrlForbidden),
            },
            intl.formatMessage(messages.infoToastError),
          ));
          return;
        }

        // Map a server 400 onto the same field errors as client validation;
        // anything else surfaces as a generic error toast.
        if (status === 400 && errorPayload && typeof errorPayload === 'object') {
          const fields = errorPayload as Record<string, unknown>;
          let mapped = false;
          if ('name' in fields) {
            setNameError(intl.formatMessage(
              trimmedName ? messages.infoNameTooLong : messages.infoNameRequired,
            ));
            mapped = true;
          }
          if ('description' in fields) {
            setDescriptionError(intl.formatMessage(messages.infoDescriptionTooLong));
            mapped = true;
          }
          if (mapped) {
            return;
          }
        }
        setNotification({ success: false, msg: intl.formatMessage(messages.infoToastError) });
      },
    );
  };

  const createdDate = intl.formatDate(detail.created, {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  const readonlyDescription = detail.description ? (
    <span className="cohort-info-description">{detail.description}</span>
  ) : (
    <span className="cohort-info-description cohort-info-muted">
      {intl.formatMessage(messages.infoNoDescription)}
    </span>
  );

  return (
    <div className="cohort-info">
      <SlAlert
        ref={notificationRef}
        variant={notification?.success ? 'success' : 'danger'}
        duration={3000}
        closable
      >
        <SlIcon slot="icon" name={notification?.success ? 'check2-circle' : 'exclamation-octagon'} />
        <strong>{notification?.msg}</strong>
      </SlAlert>

      <div className="cohort-info-card">
        {isEditing ? (
          <div className="cohort-info-card-title">
            {intl.formatMessage(messages.infoEditTitle)}
          </div>
        ) : (canEdit && (
          <div className="cohort-info-card-actions">
            <button
              type="button"
              className="cohort-info-edit-button"
              onClick={startEdit}
            >
              {intl.formatMessage(messages.infoEdit)}
            </button>
          </div>
        ))}

        <InfoField label={intl.formatMessage(messages.infoLabelName)}>
          {isEditing ? (
            <>
              <input
                id="cohort-edit-name"
                className="cohort-info-edit-input"
                type="text"
                value={editName}
                aria-label={intl.formatMessage(messages.infoLabelName)}
                aria-invalid={nameError ? true : undefined}
                aria-describedby={nameError ? 'cohort-edit-name-error' : undefined}
                onChange={(e) => { setEditName(e.target.value); setNameError(null); }}
              />
              {nameError && (
                <span id="cohort-edit-name-error" className="form-error form-error-custom" role="alert">
                  {nameError}
                </span>
              )}
            </>
          ) : (
            <span className="cohort-info-name">{detail.name}</span>
          )}
        </InfoField>

        <InfoField label={intl.formatMessage(messages.infoLabelDescription)}>
          {isEditing ? (
            <>
              <textarea
                id="cohort-edit-description"
                className="cohort-info-edit-textarea"
                rows={5}
                value={editDescription}
                aria-label={intl.formatMessage(messages.infoLabelDescription)}
                aria-invalid={descriptionError ? true : undefined}
                aria-describedby={descriptionError ? 'cohort-edit-description-error' : undefined}
                onChange={(e) => { setEditDescription(e.target.value); setDescriptionError(null); }}
              />
              {descriptionError && (
                <span
                  id="cohort-edit-description-error"
                  className="form-error form-error-custom"
                  role="alert"
                >
                  {descriptionError}
                </span>
              )}
            </>
          ) : readonlyDescription}
        </InfoField>

        {isEditing && canEditUrl && (
          <InfoField label={intl.formatMessage(messages.infoLabelUrl)}>
            <input
              id="cohort-edit-slug"
              className="cohort-info-edit-input"
              type="text"
              value={editSlug}
              aria-label={intl.formatMessage(messages.infoLabelUrl)}
              aria-invalid={slugError ? true : undefined}
              aria-describedby={slugError
                ? 'cohort-edit-slug-hint cohort-edit-slug-error'
                : 'cohort-edit-slug-hint'}
              onChange={(e) => { setEditSlug(e.target.value); setSlugError(null); }}
            />
            <span id="cohort-edit-slug-hint" className="cohort-info-url-hint">
              {intl.formatMessage(messages.infoUrlHint)}
            </span>
            <span className="cohort-info-url-preview">{buildInviteLink(trimmedSlug || detail.slug)}</span>
            {slugHasChanged && (
              <span className="cohort-info-url-warning" role="alert">
                <SlIcon name="exclamation-triangle" aria-hidden="true" />
                {' '}
                {intl.formatMessage(messages.infoUrlChangeWarning)}
              </span>
            )}
            {slugError && (
              <span id="cohort-edit-slug-error" className="form-error form-error-custom" role="alert">
                {slugError}
              </span>
            )}
          </InfoField>
        )}

        <InfoField label={intl.formatMessage(messages.infoLabelCourse)}>
          {intl.formatMessage(messages.infoProgramName)}
        </InfoField>

        <InfoField label={intl.formatMessage(messages.infoLabelFacilitator)}>
          <div className="cohort-info-facilitator">
            {ownerName && (
              <div
                className="cohort-info-avatar"
                style={{ backgroundColor: avatarColor(ownerName) }}
                aria-hidden="true"
              >
                {initialsOf(ownerName)}
              </div>
            )}
            <span className="cohort-info-facilitator-name">{ownerName || '—'}</span>
          </div>
        </InfoField>

        <InfoField label={intl.formatMessage(messages.infoLabelMembers)}>
          {intl.formatMessage(messages.infoMembersValue, { count: detail.member_count })}
        </InfoField>

        <InfoField label={intl.formatMessage(messages.infoLabelCreated)}>
          {createdDate}
        </InfoField>

        {isEditing && (
          <div className="cohort-info-edit-buttons">
            <button
              type="button"
              className="leg-primary-button"
              onClick={handleSave}
              disabled={isSaving}
            >
              {intl.formatMessage(messages.infoSave)}
            </button>
            <button
              type="button"
              className="cohort-info-cancel-button"
              onClick={cancelEdit}
              disabled={isSaving}
            >
              {intl.formatMessage(messages.infoCancel)}
            </button>
          </div>
        )}
      </div>

      <div className="cohort-info-invite">
        <label className="cohort-info-invite-label" htmlFor="cohort-invite-link">
          {intl.formatMessage(messages.inviteLabel)}
        </label>
        <div className="cohort-info-invite-row">
          <input
            id="cohort-invite-link"
            className="cohort-info-invite-input"
            type="text"
            value={inviteLink}
            readOnly
          />
          <button
            type="button"
            className="leg-primary-button cohort-info-invite-copy"
            onClick={handleCopyLink}
          >
            {intl.formatMessage(messages.inviteCopyButton)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CohortInfo;
